import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAssistantState } from '@assistant-ui/react';

import thumbDown from '@atom/components/common/svgIcons/thumbDown.svg';
import thumbDownOffAlt from '@atom/components/common/svgIcons/thumbDownOffAlt.svg';
import thumbUp from '@atom/components/common/svgIcons/thumbUp.svg';
import thumbUpOffAlt from '@atom/components/common/svgIcons/thumbUpOffAlt.svg';
import { Button, Icon, IconButton, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import { AIChatMessageFeedbackSentiment } from '@atom/types/ai';

import { useChatContext } from '../../ChatContext';
import { ChatAssistantMessageHookResponse } from '../types';

import classes from '../../chat.module.css';

const styles = {
  icon: {
    fontSize: '1rem',
    width: '1rem',
    height: '1rem',
  },
  input: {
    borderRadius: '4px',
    background: colors.neutral.white,
    height: '2.25rem',
  },
  button: {
    borderRadius: '8px',
    textTransform: 'initial',
  },
};

export const useFeedbackAction = (): ChatAssistantMessageHookResponse => {
  const {
    feedbackData: { feedback: allFeedback, onFeedbackGiven },
  } = useChatContext();
  const message = useAssistantState(state => state.message);
  const [loading, setLoading] = useState<boolean>(false);
  const [showComment, setShowComment] = useState<boolean>(false);
  const [sentiment, setSentiment] =
    useState<AIChatMessageFeedbackSentiment | null>(null);
  const [comment, setComment] = useState<string>('');

  const feedback = allFeedback[message.id];

  useEffect(() => {
    if (feedback) {
      setSentiment(feedback.sentiment);
    }
  }, [feedback]);

  const resetCommentState = useCallback(() => {
    setLoading(false);
    setShowComment(false);
    setComment('');
  }, []);

  const handleThumbsDownClick = useCallback(async () => {
    if (sentiment === 'negative') {
      return;
    }

    setSentiment('negative');
    setShowComment(true);
    await onFeedbackGiven(message.id, 'negative');
  }, [sentiment, message.id, onFeedbackGiven]);

  const handleThumbsUpClick = useCallback(async () => {
    if (sentiment === 'positive') {
      return;
    }

    setSentiment('positive');
    setShowComment(true);
    await onFeedbackGiven(message.id, 'positive');
  }, [sentiment, message.id, onFeedbackGiven]);

  const handleCommentChange = useCallback((event: any) => {
    setComment(event.target.value);
  }, []);

  const handleCloseComment = useCallback(() => {
    resetCommentState();
  }, [resetCommentState]);

  const handleCommentSubmit = useCallback(async () => {
    setLoading(true);
    await onFeedbackGiven(message.id, sentiment, comment);
    resetCommentState();
  }, [message.id, sentiment, comment, onFeedbackGiven, resetCommentState]);

  const content = useMemo(() => {
    if (!showComment) {
      return null;
    }

    return (
      <div className={classes.commentSection}>
        <div className={classes.commentHeader}>
          <div className={classes.commentTitle}>Thanks for your rating</div>
          <IconButton size="small" onClick={handleCloseComment}>
            <Icon color={colors.neutral.gray}>close</Icon>
          </IconButton>
        </div>
        <div className={classes.commentSubtitle}>
          {sentiment === 'positive'
            ? "We'd love to hear what you liked about this response."
            : 'Your feedback helps us make Atom AI work better for you.'}
        </div>
        <div className={classes.commentInputWrapper}>
          <TextField
            variant="outlined"
            value={comment || ''}
            onChange={handleCommentChange}
            placeholder="Provide additional feedback (optional)"
            slotProps={{
              input: {
                style: styles.input,
              },
            }}
          />
        </div>
        <div className={classes.commentButtons}>
          <Button
            style={styles.button}
            variant="text"
            onClick={handleCloseComment}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            style={styles.button}
            onClick={handleCommentSubmit}
            disabled={!comment || loading}
          >
            Submit
          </Button>
        </div>
      </div>
    );
  }, [
    showComment,
    sentiment,
    comment,
    loading,
    handleCloseComment,
    handleCommentChange,
    handleCommentSubmit,
  ]);

  return useMemo<ChatAssistantMessageHookResponse>(
    () => ({
      available: true,
      type: 'standalone',
      ui: [
        {
          icon: (
            <img
              src={sentiment === 'negative' ? thumbDown : thumbDownOffAlt}
              style={styles.icon}
            />
          ),
          copy: 'Bad response',
          onClick: handleThumbsDownClick,
        },
        {
          icon: (
            <img
              src={sentiment === 'positive' ? thumbUp : thumbUpOffAlt}
              style={styles.icon}
            />
          ),
          copy: 'Good response',
          onClick: handleThumbsUpClick,
        },
      ],
      content,
    }),
    [sentiment, handleThumbsDownClick, handleThumbsUpClick, content],
  );
};
